/**
 * EditorialInstrumentPanel — the "instrument-panel" KPI-hero block: a hairline
 * top-rule, a mono small-caps header (+ optional status chip), dotted-leader
 * sub-rows, a serif HERO value, and an optional caveat footnote.
 *
 * Reference: `~/Desktop/Redhead_BTM_Feasibility_Redesign_1.pdf` —
 *   p5 "CAPACITY CREDIT - ELCC" panel → header + IT/FACILITY/MVA/RESERVE
 *       sub-rows + the "FIRM-DELIVERABLE (ELCC) 48.5 MW" hero + a caveat;
 *   p6 "NEAREST IX  204.9 km" card → header + hero + sub.
 *
 * FLATTEN TO CREAM (Decision C, 2026-06-16) — the panel was previously a deep
 * near-black instrument card; it is now a BARE ink-on-cream KPI-hero. A
 * hairline TOP rule frames it (no fill, no card); all text/rule colours route
 * through `roleColor` (light). The serif hero numeral takes the STATUS ACCENT —
 * AMBER (warning) when the status chip is amber/pending, TEAL (accent)
 * otherwise — NOT the call site's green/red heroTone. Sub-rows are dotted-
 * leader key→value (mono), dropping empty values so the panel never fabricates
 * a spec — matching EditorialDatasheet / EditorialKpiRail.
 *
 * Composes EditorialHeroMetric (surface='light') for the hero and
 * EditorialStatusChip (surface='light') when a verdict/signal chip is supplied.
 *
 * Pure / print-safe (no shadow, no animation). Returns null when there is no
 * hero value AND no renderable sub-row, so callers can compose it conditionally.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialHeroMetric, type EditorialHeroTone } from './EditorialHeroMetric';
import { EditorialStatusChip, type EditorialChipStatus } from './EditorialStatusChip';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface InstrumentRow {
  key: React.ReactNode;
  /** `null`/`undefined`/'' drops the row (no fabricated spec). */
  value: React.ReactNode;
}

export interface EditorialInstrumentPanelProps {
  theme: ReportTheme;
  /** Mono small-caps panel header, e.g. "Capacity Credit · ELCC". */
  heading: React.ReactNode;
  /** Optional sub-rows ABOVE the hero (the IT→FACILITY→MVA breakdown). */
  rows?: InstrumentRow[];
  /** The serif hero value, e.g. "48.5", "204.9". '' renders no hero. */
  heroValue?: React.ReactNode;
  /** Hero unit, e.g. "MW", "km". */
  heroUnit?: React.ReactNode;
  /** Hero label (mono small-caps), e.g. "Firm-Deliverable (ELCC)". */
  heroLabel?: React.ReactNode;
  /** Retint the hero value to a status role. */
  heroTone?: EditorialHeroTone;
  /** Optional verdict/signal chip in the header (e.g. 'pending'). */
  chip?: EditorialChipStatus;
  /** Optional chip label override. */
  chipLabel?: React.ReactNode;
  /** Optional faint caveat footnote (mono). */
  caveat?: React.ReactNode;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

function isRenderableValue(value: React.ReactNode): boolean {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function EditorialInstrumentPanel({
  theme,
  heading,
  rows = [],
  heroValue,
  heroUnit,
  heroLabel,
  // `heroTone` is accepted for call-site back-compat but intentionally NOT
  // applied to the hero numeral on the light skin — Decision C drives the
  // numeral colour from the status chip (amber/teal), see heroAccentTone.
  heroTone: _heroTone = 'default',
  chip,
  chipLabel,
  caveat,
  id,
  className,
  style,
}: EditorialInstrumentPanelProps): React.ReactElement | null {
  const renderRows = rows.filter((r) => isRenderableValue(r.value));
  const hasHero = isRenderableValue(heroValue);
  if (!hasHero && renderRows.length === 0) return null;

  // DEFECT 6 / Decision C — BARE ink-on-cream KPI-hero (was a near-black
  // instrument card). Light tokens via roleColor; a hairline TOP rule frames
  // the panel; the mono small-caps header + light status chip ride the top;
  // dotted-leader sub-rows read key→value; the big serif hero numeral takes
  // the STATUS ACCENT — AMBER (warning) when the status is amber/pending, TEAL
  // (accent) otherwise. No black, no framed card.
  const headInk = roleColor(theme, 'muted');
  const rowKeyInk = roleColor(theme, 'muted');
  const rowValInk = roleColor(theme, 'ink');
  const hairRule = roleColor(theme, 'hair');
  const topRule = roleColor(theme, 'ink');
  const caveatInk = roleColor(theme, 'faint');
  const leaderInk = roleColor(theme, 'hair');

  // Decision C — the hero numeral colour reflects STATUS, not the call site's
  // green/red: AMBER status → warning (amber); everything else → accent (teal).
  const amberStatus = chip === 'amber' || chip === 'pending';
  const heroAccentTone: EditorialHeroTone = amberStatus ? 'warning' : 'accent';

  return (
    // J1 — atomic print/paged block: the panel never splits across a page foot.
    <div
      id={id}
      className={className}
      data-report-block="instrument-panel"
      data-layout-group="exhibit"
      data-surface="light"
      style={{
        // Hairline top-rule, bare on cream (no fill, no framed card).
        borderTop: `2px solid ${topRule}`,
        paddingTop: theme.density === 'compact' ? 10 : 12,
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          fontFamily: MONO,
          fontSize: REPORT_TEXT_PX.caption,
          fontWeight: 600,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: headInk,
        }}
      >
        <span>{heading}</span>
        {chip != null && (
          <EditorialStatusChip theme={theme} status={chip} surface="light">
            {chipLabel}
          </EditorialStatusChip>
        )}
      </div>

      {renderRows.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {renderRows.map((row, r) => (
            <div
              key={r}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                padding: '4px 0',
                fontFamily: MONO,
                fontSize: 11.5,
              }}
            >
              <span style={{ color: rowKeyInk, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                {row.key}
              </span>
              {/* Dotted leader filling the gap between key and value — the
                  classic datasheet rail. */}
              <span
                aria-hidden
                style={{ flex: '1 1 auto', borderBottom: `1px dotted ${leaderInk}`, transform: 'translateY(-3px)', minWidth: 12 }}
              />
              <span style={{ color: rowValInk, fontWeight: 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {hasHero && (
        <EditorialHeroMetric
          theme={theme}
          surface="light"
          size="lg"
          label={heroLabel ?? ''}
          value={heroValue}
          unit={heroUnit}
          // Decision C — hero numeral in the status accent (amber/teal). The
          // call site's `heroTone` (green/red) is intentionally NOT used for
          // the numeral colour on the light skin — see heroAccentTone above.
          tone={heroAccentTone}
          style={{ marginTop: renderRows.length > 0 ? 14 : 10 }}
        />
      )}

      {isRenderableValue(caveat) && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '0.03em',
            lineHeight: 1.5,
            color: caveatInk,
            marginTop: hasHero || renderRows.length > 0 ? 10 : 0,
            paddingTop: 6,
            borderTop: `1px solid ${hairRule}`,
          }}
        >
          {caveat}
        </div>
      )}
    </div>
  );
}
